/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Video,
  Users,
  Ticket,
  Eye,
  Star,
  ExternalLink,
  Loader2,
  CalendarX,
  GraduationCap,
  Mail,
} from "lucide-react";
import { Card, CardContent } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Event } from "../../../../api";
import { buildStorageUrl, formatEventDate, formatEventTime12h } from "../../../../utils";
import { useSmoothNavigate } from "../../../../utils/landing/useSmoothNavigate";
import "./EventDetail.scss";

const eventController = new Event();

const STATUS_LABELS = {
  draft: "Borrador",
  published: "Publicado",
  cancelled: "Cancelado",
  completed: "Finalizado",
};

const STATUS_STYLES = {
  draft: "bg-gray-500",
  published: "bg-emerald-600",
  cancelled: "bg-red-600",
  completed: "bg-blue-600",
};

const AUDIENCE_LABELS = {
  all: "Todo público",
  students: "Estudiantes",
  teachers: "Docentes",
  parents: "Padres de familia",
  public: "Público en general",
};

function InfoRow({ icon, label, children }) {
  return (
    <div className="flex gap-3">
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#4a321c]/10 text-[#4a321c]">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
          {label}
        </p>
        <div className="text-sm font-medium text-gray-800">{children}</div>
      </div>
    </div>
  );
}

export function EventDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const smoothNavigate = useSmoothNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvent();
  }, [slug]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      const response = await eventController.getPublicEventBySlug(slug);
      setEvent(response.data);
    } catch (error) {
      console.error("Error loading event:", error);
      setEvent(null);
    } finally {
      setLoading(false);
    }
  };

  const formatFullDate = (date) => {
    try {
      const formatted = formatEventDate(date, "EEEE dd 'de' MMMM 'de' yyyy");
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    } catch {
      return "Fecha no disponible";
    }
  };

  const formatTime = (date) => {
    try {
      return formatEventTime12h(date);
    } catch {
      return "";
    }
  };

  const getImageUrl = () => buildStorageUrl(event?.imageUrl);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-20">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-10 w-10 animate-spin text-[#4a321c]" />
          <p className="text-sm text-gray-500">Cargando evento...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4 pt-20">
        <div className="flex max-w-md flex-col items-center gap-4 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
            <CalendarX className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-[#4a321c]">
            Evento no encontrado
          </h1>
          <p className="text-gray-600">
            El evento que buscas no existe, no está publicado o ya no está
            disponible.
          </p>
          <Button
            onClick={() => navigate("/")}
            className="bg-[#4a321c] text-white hover:bg-[#5b4229]"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  const imageUrl = getImageUrl();
  const organizerName = event.User
    ? [event.User.firstname, event.User.lastname].filter(Boolean).join(" ")
    : null;
  const tags = event.tags
    ? String(event.tags)
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean)
    : [];

  return (
    <div className="bg-[#fdf6f0]">
      <div className="mx-auto max-w-5xl px-4 pb-8 pt-24">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => smoothNavigate(e, "/#eventos")}
          className="mb-4 text-[#4a321c] hover:bg-[#4a321c]/10"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>

        <div className="relative mb-8 overflow-hidden rounded-2xl shadow-lg">
          <div className="relative h-64 w-full sm:h-80 md:h-[420px]">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={event.title}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#4a321c] to-[#8a6440]">
                <Calendar className="h-20 w-20 text-white/40" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

            {event.featured && (
              <div className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-yellow-400 px-3 py-1 text-xs font-semibold text-yellow-900 shadow">
                <Star className="h-3.5 w-3.5 fill-current" />
                Destacado
              </div>
            )}

            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                {event.EventCategory && (
                  <span
                    className="rounded-full px-3 py-1 text-xs font-semibold text-white shadow"
                    style={{
                      backgroundColor: event.EventCategory.color || "#4CAF50",
                    }}
                  >
                    {event.EventCategory.name}
                  </span>
                )}
                {event.status !== "published" && (
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold text-white shadow ${
                      STATUS_STYLES[event.status] || "bg-gray-500"
                    }`}
                  >
                    {STATUS_LABELS[event.status] || event.status}
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl font-bold leading-tight text-white sm:text-3xl md:text-4xl">
                {event.title}
              </h1>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {event.content && (
              <Card className="border border-[#e6d5ba] bg-white shadow-md">
                <CardContent className="p-6 md:p-8">
                  <h2 className="mb-4 text-lg font-bold text-[#4a321c]">
                    Sobre el evento
                  </h2>
                  <div
                    className="event-detail__content"
                    dangerouslySetInnerHTML={{ __html: event.content }}
                  />
                </CardContent>
              </Card>
            )}

            {(event.AcademicCycle || event.Grade || tags.length > 0) && (
              <Card className="border border-[#e6d5ba] bg-white shadow-md">
                <CardContent className="flex flex-wrap items-center gap-2 p-6">
                  {event.AcademicCycle && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#4a321c]/10 px-3 py-1 text-xs font-medium text-[#4a321c]">
                      <GraduationCap className="h-3.5 w-3.5" />
                      {event.AcademicCycle.name}
                    </span>
                  )}
                  {event.Grade && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#4a321c]/10 px-3 py-1 text-xs font-medium text-[#4a321c]">
                      {event.Grade.name}
                    </span>
                  )}
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600"
                    >
                      #{tag}
                    </span>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-4">
            <Card className="border border-[#e6d5ba] bg-white shadow-md">
              <CardContent className="space-y-5 p-6">
                <InfoRow icon={<Calendar className="h-5 w-5" />} label="Fecha">
                  {formatFullDate(event.eventDate)}
                </InfoRow>

                <InfoRow icon={<Clock className="h-5 w-5" />} label="Hora">
                  {formatTime(event.eventDate)}
                  {event.endDate && ` - ${formatTime(event.endDate)}`}
                </InfoRow>

                {event.isVirtual ? (
                  <InfoRow icon={<Video className="h-5 w-5" />} label="Modalidad">
                    <span>Evento virtual</span>
                    {event.virtualLink && (
                      <a
                        href={event.virtualLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-[#4a321c] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#5b4229]"
                      >
                        Ir al enlace <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </InfoRow>
                ) : (
                  event.location && (
                    <InfoRow icon={<MapPin className="h-5 w-5" />} label="Ubicación">
                      {event.location}
                    </InfoRow>
                  )
                )}

                <InfoRow icon={<Users className="h-5 w-5" />} label="Dirigido a">
                  {AUDIENCE_LABELS[event.targetAudience] || event.targetAudience}
                </InfoRow>

                {event.maxAttendees && (
                  <InfoRow icon={<Ticket className="h-5 w-5" />} label="Cupo">
                    {event.maxAttendees} personas
                  </InfoRow>
                )}

                {event.registrationRequired && (
                  <InfoRow
                    icon={<Ticket className="h-5 w-5" />}
                    label="Registro requerido"
                  >
                    {event.registrationDeadline
                      ? `Hasta el ${formatEventDate(
                          event.registrationDeadline,
                          "dd/MM/yyyy"
                        )}`
                      : "Sí"}
                  </InfoRow>
                )}

                {organizerName && (
                  <InfoRow icon={<Mail className="h-5 w-5" />} label="Organiza">
                    {organizerName}
                  </InfoRow>
                )}
              </CardContent>
            </Card>

            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400">
              <Eye className="h-3.5 w-3.5" />
              {event.viewsCount} visualización{event.viewsCount !== 1 ? "es" : ""}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
